import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface RealisticaFullAnimationsProps {
  area: string;
  location: string;
  price: number;
  beds: number;
  bath: number;
  parking: string;
  terrace: string;
  projectStatus: string;
  isNeubauprojekt: boolean;
  projectName: string;
  projectType: string;
  heroImage: string;
  energyStatus: number;
  marketMinPrice: number;
  marketMaxPrice: number;
  finishesImages: string[];
  marketCompareSvg: string;
  marketCompareColorSvg: string;
}

const FutureProjectsPage: React.FC<RealisticaFullAnimationsProps> = ({
  area,
  location,
  price,
  beds,
  bath,
  parking,
  terrace,
  projectStatus,
  isNeubauprojekt,
  projectName,
  projectType,
  heroImage,
  energyStatus,
  marketMinPrice,
  marketMaxPrice,
  finishesImages,
  marketCompareSvg,
  marketCompareColorSvg,
}) => {
  const heroMaskRef = useRef<HTMLDivElement | null>(null);
  const headerLinesRef = useRef<(HTMLDivElement | HTMLHeadingElement)[]>([]); // Corrected type
  const marqueeWrapperRef = useRef<HTMLDivElement | null>(null);
  const dropInTextRefs = useRef<HTMLElement[]>([]); // Use HTMLElement for broader compatibility
  const energyStatusRef = useRef<HTMLDivElement | null>(null);
  const finishesSectionRef = useRef<HTMLElement | null>(null); // Use HTMLElement
  const finishesImagesRefs = useRef<HTMLImageElement[]>([]);
  const marketCompareSectionRef = useRef<HTMLElement | null>(null); // Use HTMLElement
  const marketCompareMaskRef = useRef<HTMLDivElement | null>(null);
  const marketHeaderRef = useRef<HTMLDivElement | null>(null); // Corrected type to HTMLDivElement
  const marketParagraphRef = useRef<HTMLDivElement | null>(null); // Corrected type to HTMLDivElement
  const marketMinPriceRef = useRef<HTMLDivElement | null>(null);
  const marketMaxPriceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Hero section mask animation
    if (heroMaskRef.current) {
      gsap.fromTo(
        heroMaskRef.current,
        { yPercent: 101 },
        { yPercent: 0, duration: 1.2, ease: 'power2.out' }
      );
    }

    // Header lines animation
    if (headerLinesRef.current.length > 0) {
      gsap.fromTo(
        headerLinesRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power1.out',
          stagger: 0.1,
        }
      );
    }

    // Marquee button animation
    if (marqueeWrapperRef.current) {
      gsap.to(marqueeWrapperRef.current, {
        x: '-624px', // Adjust this value based on the actual width of your content
        duration: 8,
        ease: 'linear',
        repeat: -1,
      });
    }

    // Drop-in text animation
    if (dropInTextRefs.current.length > 0) {
      dropInTextRefs.current.forEach((el) => {
        if (el) {
          gsap.fromTo(
            el,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power1.out',
              scrollTrigger: {
                trigger: el,
                start: 'top 85%',
              },
            }
          );
        }
      });
    }

    // Energy/status bar animation
    if (energyStatusRef.current) {
      const statusNumber = energyStatusRef.current;
      const targetNumber = energyStatus;
      const duration = 1.5;

      ScrollTrigger.create({
        trigger: statusNumber,
        start: 'top 85%',
        onEnter: () => {
          gsap.to({ count: 0 }, {
            count: targetNumber,
            duration: duration,
            onUpdate: function() {
              statusNumber.textContent = Math.round(this.targets()[0].count).toString();
            },
            ease: 'power1.out',
          });
        },
      });
    }

    // Finishes section images animation
    if (finishesSectionRef.current && finishesImagesRefs.current.length > 0) {
        const initialYOffsets = [160, 120, 60, 50, -60, 400]; // Corresponding to spf-img-1 to spf-img-6

        finishesImagesRefs.current.forEach((img, index) => {
            if (img) {
                gsap.fromTo(img,
                    { y: initialYOffsets[index] || 0, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 1.2,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: finishesSectionRef.current,
                            start: 'top 85%',
                        }
                    }
                );
            }
        });
    }


    // Market Compare animations
    if (marketCompareSectionRef.current) {
      // SVG mask animation
      if (marketCompareMaskRef.current) {
        gsap.fromTo(
          marketCompareMaskRef.current,
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: 1.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: marketCompareSectionRef.current,
              start: 'top 85%',
            },
          }
        );
      }

      // Header and paragraph animation
      if (marketHeaderRef.current && marketParagraphRef.current) {
        gsap.fromTo(
          [marketHeaderRef.current, marketParagraphRef.current],
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power1.out',
            stagger: 0.1,
            scrollTrigger: {
              trigger: marketCompareSectionRef.current,
              start: 'top 85%',
            },
          }
        );
      }

      // Price count up animation
      const formatPrice = (number: number) => {
        return number.toLocaleString('en-US').replace(/,/g, '.') + ' $';
      };

      if (marketMinPriceRef.current) {
        const minPriceEl = marketMinPriceRef.current;
        const targetMinPrice = marketMinPrice;
        ScrollTrigger.create({
          trigger: minPriceEl,
          start: 'top 85%',
          onEnter: () => {
            gsap.to({ count: 0 }, {
              count: targetMinPrice,
              duration: 1.5,
              onUpdate: function() {
                minPriceEl.textContent = formatPrice(Math.round(this.targets()[0].count));
              },
              ease: 'power1.out',
            });
          },
        });
      }

      if (marketMaxPriceRef.current) {
        const maxPriceEl = marketMaxPriceRef.current;
        const targetMaxPrice = marketMaxPrice;
        ScrollTrigger.create({
          trigger: maxPriceEl,
          start: 'top 85%',
          onEnter: () => {
            gsap.to({ count: 0 }, {
              count: targetMaxPrice,
              duration: 1.5,
              onUpdate: function() {
                maxPriceEl.textContent = formatPrice(Math.round(this.targets()[0].count));
              },
              ease: 'power1.out',
            });
          },
        });
      }
    }

  }, [area, location, price, beds, bath, parking, terrace, projectStatus, isNeubauprojekt, projectName, projectType, heroImage, energyStatus, marketMinPrice, marketMaxPrice, finishesImages, marketCompareSvg, marketCompareColorSvg]); // Add props as dependencies

  // Helper function to add refs to elements for GSAP toArray
  const addToRefs = (el: HTMLElement | null, refArray: React.MutableRefObject<HTMLElement[]>) => {
    if (el && !refArray.current.includes(el)) {
      refArray.current.push(el);
    }
  };

  // Reset refs on each render to avoid issues with dynamic lists
  useEffect(() => {
    headerLinesRef.current = [];
    dropInTextRefs.current = [];
    finishesImagesRefs.current = [];
  });


  return (
    <div>
      {/* Hero Section */}
      <div className="section">
        <div className="single-project-hero">
          <div className="sph-image-wrapper">
            <img src={heroImage} alt="" className="image" />
            <div className="mask-image bg-light-black" ref={heroMaskRef} style={{ transform: 'translateY(101%)' }}></div>
          </div>
          <div className="sph-content">
            <div className="sph-header" ref={marketHeaderRef}> {/* Reusing class for animation */}
              <div className="paragraph-12px all-caps" ref={(el) => addToRefs(el, headerLinesRef)}>{projectType}</div>
              <div className="paragraph-12px" ref={(el) => addToRefs(el, headerLinesRef)}>/</div>
              <div className="paragraph-12px all-caps" ref={(el) => addToRefs(el, headerLinesRef)}>{location}</div>
              <div className="paragraph-12px" ref={(el) => addToRefs(el, headerLinesRef)}>/</div>
            </div>
            <h1 className="display-small white" ref={(el) => addToRefs(el, headerLinesRef)}>{projectName}</h1>
            {/* Add other hero bottom elements like wishlist, download, contact button if needed */}
          </div>
        </div>
      </div>

      {/* Property Details Section */}
      <div className="section">
        <div className="container">
          <div className="single-project-details flex flex-col space-y-0.5"> {/* Adjusted spacing */}
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{projectType}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>/</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{location}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>/</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{projectName}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{area} sq.ft.</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{location}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
              {projectStatus} {isNeubauprojekt && '/ Neubauprojekt'}
            </div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>{price.toLocaleString('en-US').replace(/,/g, '.')} $</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>beds {beds}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>bath {bath}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>parking {parking}</div>
            <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>terrace {terrace} sq.ft.</div>
          </div>
        </div>
      </div>

      {/* Header lines (already included in Hero section for this template) */}

      {/* Marquee button */}
      <div className="section"> {/* Wrap in a section if needed for layout */}
        <div className="container"> {/* Wrap in a container if needed for layout */}
          <div className="nav-menu-btn-contact w-inline-block"> {/* Assuming this is the button wrapper */}
            <div className="nav-menu-btn-infinite-loop">
              <div className="nav-menu-btn-wrapper" ref={marqueeWrapperRef}>
                {/* Duplicate buttons/text for marquee effect */}
                <p className="paragraph-16px regular difference">Schedule a meeting</p>
                <p className="paragraph-16px regular difference">Schedule a meeting</p>
                <p className="paragraph-16px regular difference">Schedule a meeting</p>
                <p className="paragraph-16px regular difference">Schedule a meeting</p>
              </div>
              {/* Add another duplicate wrapper if needed for seamless loop */}
            </div>
          </div>
        </div>
      </div>


      {/* Drop-in text (applied to various elements) */}
      {/* Example: */}
      <div className="section">
        <div className="container">
          <div className="single-project-about flex flex-col space-y-2"> {/* Adjusted spacing */}
            <div className="spa-heading flex flex-col space-y-1"> {/* Adjusted spacing */}
              <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
                <div className="paragraph-14px white">Project</div>
                <div className="paragraph-16px white">{projectName}</div>
              </div>
              <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
                <div className="paragraph-14px white">Neighborhood</div>
                <div className="paragraph-16px white">{location}</div>
              </div>
            </div>
            <div className="spa-content">
              <div className="drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
                <h6 className="title-h6 white">Lorem ipsum dolor sit amet consectetur. Tempor sed ornare non dolor dolor sapien scelerisque. Lobortis hendrerit ipsum turpis malesuada mauris. Fames mattis enim id id euismod. Amet arcu orci ullamcorper lacinia phasellus massa. Sed sed quisque fermentum enim lacus ornare. </h6>
              </div>
              {/* Add other drop-in text elements as needed */}
            </div>
          </div>
        </div>
      </div>


      {/* Energy/status bar */}
      <div className="section">
        <div className="container spe">
          <section className="single-project-energy _2">
            <div className="spe-wrapper">
              <div className="spe-text" ref={energyStatusRef}>0</div> {/* Initial text for counting */}
              <div className="spe-image-wrapper">
                {/* Replace with your energy image */}
                <img src="/path/to/your/energy-image.png" alt="" className="image" />
              </div>
            </div>
            <div className="spe-text-wrapper drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
              <div className="spe-heading">discover our new green houses</div>
              <div className="paragraph-16px white regular">The towers have recognisable family features – the visual division into three tiers, the brick facades, the same height and cross-section – but each one has its own individual character, facade plasticity, details and its own colour scheme.</div>
            </div>
          </section>
        </div>
      </div>

      {/* Finishes section */}
      <div className="section">
        <div className="container spf">
          <section className="finishes-section" ref={finishesSectionRef}>
            <div className="spf-header drop-in-text" ref={(el) => addToRefs(el, dropInTextRefs)}>
              <h1 className="title-80px white">Finishes</h1>
              <div className="spf-header-wrapper">
                <div className="paragraph-14px white">(01)</div>
                <div className="paragraph-16px white">Package I</div>
              </div>
            </div>
            <div className="spf-content">
              <div className="spf-images">
                {finishesImages.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Finishes ${index + 1}`}
                    className={`spf-img-${index + 1}`}
                    ref={(el) => addToRefs(el, finishesImagesRefs)}
                    style={{ position: 'absolute', opacity: 0 }} // Initial styles for animation
                  />
                ))}
              </div>
              {/* Add other finishes content */}
            </div>
          </section>
        </div>
      </div>

      {/* Market Compare */}
      <div className="section">
        <div className="container">
          <section className="single-project-market" ref={marketCompareSectionRef}>
            <div className="spm-header">
              <div className="mask">
                <h1 className="title-80px white" ref={marketHeaderRef}>Market Compare</h1>
              </div>
              <div className="paragraph-14px white" ref={marketParagraphRef}>We may use the information you provide to inform you or contact you regarding products and services that we feel would be of interest to you. We may share your information with our affiliates and partners for the same purposes. We may retain your information to facilitate contact, or to provide service to you in the future.</div>
            </div>
            <div className="spm-content">
              <div className="spm-image-wrapper">
                {/* Replace with your market diagram images */}
                <img src={marketCompareSvg} alt="Market Price" className="spm-image" />
                <div className="spm-mask" ref={marketCompareMaskRef}>
                  <img src={marketCompareColorSvg} alt="Market Price" className="spm-image-absolute" style={{ transform: 'translateY(100%)' }} /> {/* Initial style for animation */}
                </div>
              </div>
              <div className="spm-wrapper">
                <div className="spm-inner">
                  <div className="spm-text">Market Min. Price</div>
                  <div className="paragraph-16px white" ref={marketMinPriceRef}>0 $</div> {/* Initial text for counting */}
                </div>
                <div className="spm-inner right">
                  <div className="spm-text">Market Max. Price</div>
                  <div className="paragraph-16px white" ref={marketMaxPriceRef}>0 $</div> {/* Initial text for counting */}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Add other sections from the HTML template as needed (e.g., Plan, Surroundings, Contact Form, Footer) */}
      {/* Ensure elements within these sections that should have the drop-in animation have the class .drop-in-text */}

    </div>
  );
};

export default FutureProjectsPage;
